/**
 * <qallo-video-slider>
 * Lichte video-carrousel zonder externe libraries.
 * - Video's laden pas (src) wanneer de slide voor minstens 60% zichtbaar is én de sectie in beeld is.
 * - Enkel zichtbare slides spelen af. Alles pauzeert buiten beeld of in een verborgen tabblad.
 * - 480p bij Data Saver of 2G/3G, anders 720p.
 * - Geen autoplay bij Data Saver of prefers-reduced-motion: afspeelknop per slide.
 * - Oneindig doorscrollen: zodra het scrollen stopt, worden onzichtbare slides van de ene kant
 *   naar de andere kant verplaatst, zodat er links en rechts altijd slides klaarstaan.
 *   Bij te weinig slides om het scherm te vullen komt er één kopie van de reeks bij.
 */

const SLOW_CONNECTIONS = ['slow-2g', '2g', '3g'];
const VISIBLE_RATIO = 0.6;
const SETTLE_DELAY = 140;

class QalloVideoSlider extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.qvs__track');
    if (!this.track) return;
    this.originals = Array.from(this.track.querySelectorAll('.qvs__slide'));
    this.slides = this.originals.slice();
    this.clones = [];

    const connection = navigator.connection;
    this.saveData = Boolean(connection && connection.saveData);
    this.slowNetwork = Boolean(connection && SLOW_CONNECTIONS.includes(connection.effectiveType));
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.autoplay = this.dataset.autoplay === 'true' && !this.saveData && !reducedMotion;
    this.loopEnabled = this.dataset.loop !== 'false';

    this.inView = false;
    this.visible = new Set();
    this.userStarted = new Set();
    this.touching = false;
    this.mobileQuery = window.matchMedia('(max-width: 767px)');
    this.supportsScrollEnd = 'onscrollend' in window;

    if (!this.autoplay) {
      this.querySelectorAll('.qvs__play').forEach((button) => (button.hidden = false));
    }

    this.slideObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.intersectionRatio >= VISIBLE_RATIO) this.visible.add(entry.target);
          else this.visible.delete(entry.target);
        }
        this.sync();
      },
      { root: this.track, threshold: [0, VISIBLE_RATIO, 1] }
    );
    this.originals.forEach((slide) => this.slideObserver.observe(slide));

    this.sectionObserver = new IntersectionObserver(
      ([entry]) => {
        this.inView = entry.isIntersecting;
        this.sync();
      },
      { rootMargin: '200px 0px' }
    );
    this.sectionObserver.observe(this);

    this.onClick = this.onClick.bind(this);
    this.onVisibility = () => this.sync();
    this.onScroll = this.onScroll.bind(this);
    this.onSettle = this.onSettle.bind(this);
    this.onTouchStart = () => {
      this.touching = true;
      this.fixPending = false;
      clearTimeout(this.settleTimer);
    };
    this.onTouchEnd = () => {
      this.touching = false;
      this.scheduleSettle();
    };
    this.onBlockSelect = this.onBlockSelect.bind(this);

    this.addEventListener('click', this.onClick);
    document.addEventListener('visibilitychange', this.onVisibility);
    this.track.addEventListener('scroll', this.onScroll, { passive: true });
    if (this.supportsScrollEnd) this.track.addEventListener('scrollend', this.onSettle);
    this.track.addEventListener('touchstart', this.onTouchStart, { passive: true });
    this.track.addEventListener('touchend', this.onTouchEnd, { passive: true });
    this.track.addEventListener('touchcancel', this.onTouchEnd, { passive: true });
    if (this.hasAttribute('data-design-mode')) {
      document.addEventListener('shopify:block:select', this.onBlockSelect);
    }

    /*
     * Opbouwen (meten, kopieën, startpositie) pas wanneer de slider in de buurt van het scherm komt.
     * Meten tijdens het laden van de pagina dwingt een volledige lay-outberekening af (gemeten: ~200 ms op een trage gsm).
     */
    this.prepObserver = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        this.prepObserver.disconnect();
        this.start();
      },
      { rootMargin: '1000px 0px' }
    );
    this.prepObserver.observe(this);
  }

  start() {
    if (this.started || !this.isConnected) return;
    this.started = true;
    this.setup();

    /* Bij een andere breedte (draaien van tablet, venster aanpassen) opnieuw opbouwen. */
    this.resizeObserver = new ResizeObserver(() => {
      clearTimeout(this.resizeTimer);
      this.resizeTimer = setTimeout(() => {
        if (Math.abs(this.track.clientWidth - this.lastWidth) > 1) this.setup();
      }, 150);
    });
    this.resizeObserver.observe(this.track);
  }

  disconnectedCallback() {
    clearTimeout(this.resizeTimer);
    clearTimeout(this.settleTimer);
    this.resizeObserver?.disconnect();
    this.prepObserver?.disconnect();
    cancelAnimationFrame(this.setupFrame);
    this.started = false;
    this.slideObserver?.disconnect();
    this.sectionObserver?.disconnect();
    this.removeEventListener('click', this.onClick);
    document.removeEventListener('visibilitychange', this.onVisibility);
    document.removeEventListener('shopify:block:select', this.onBlockSelect);
    if (this.track) {
      this.track.removeEventListener('scroll', this.onScroll);
      this.track.removeEventListener('scrollend', this.onSettle);
      this.track.removeEventListener('touchstart', this.onTouchStart);
      this.track.removeEventListener('touchend', this.onTouchEnd);
      this.track.removeEventListener('touchcancel', this.onTouchEnd);
    }
    this.querySelectorAll('video').forEach((video) => video.pause());
  }

  /* ---------- Opbouw en oneindig doorscrollen ---------- */

  setup() {
    const track = this.track;
    const count = this.originals.length;

    /*
     * Eerst alle DOM-wijzigingen, zonder iets te meten. Hoeveel slides er in beeld passen,
     * komt uit de CSS-variabele --qvs-per-view (geen lay-outberekening nodig).
     */
    if (this.clones.length) {
      this.clones.forEach((clone) => {
        this.slideObserver.unobserve(clone);
        this.visible.delete(clone);
        this.userStarted.delete(clone);
        clone.querySelector('video')?.pause();
        clone.remove();
      });
      this.clones = [];
    }
    if (this.originals.some((slide, index) => track.children[index] !== slide)) {
      this.originals.forEach((slide) => track.append(slide));
    }

    const perView = parseFloat(getComputedStyle(this).getPropertyValue('--qvs-per-view')) || 1;
    const scrollable = count > 1 && count > perView + 0.01;
    this.toggleAttribute('data-scrollable', scrollable);
    this.loop = this.loopEnabled && scrollable;

    if (this.loop) {
      /* Genoeg slides voorzien: wat zichtbaar is + minstens twee reserve aan elke kant. */
      const needed = Math.ceil(perView) + 4;
      const copies = Math.max(0, Math.ceil(needed / count) - 1);
      const fragment = document.createDocumentFragment();
      for (let copy = 0; copy < copies; copy++) {
        this.originals.forEach((slide) => {
          const clone = this.makeClone(slide);
          fragment.append(clone);
          this.clones.push(clone);
        });
      }
      track.append(fragment);
      this.clones.forEach((clone) => this.slideObserver.observe(clone));

      /* Video 1 in het midden van de reeks, zodat links de laatste video al klaarstaat. */
      const total = count + this.clones.length;
      const before = Math.floor((total - 1) / 2);
      const moving = Array.from(track.children).slice(total - before);
      track.prepend(...moving);
    }
    this.slides = Array.from(track.children);

    /* Daarna één keer meten en positioneren, in het frame waarin de browser de lay-out toch berekent. */
    cancelAnimationFrame(this.setupFrame);
    this.setupFrame = requestAnimationFrame(() => {
      this.lastWidth = track.clientWidth;
      if (this.loop) this.scrollToSlide(this.originals[0], true);
      else this.jumpTo(0);
      this.updateActive();
    });
  }

  makeClone(slide) {
    const clone = slide.cloneNode(true);
    for (const attribute of Array.from(clone.attributes)) {
      if (attribute.name.startsWith('data-shopify')) clone.removeAttribute(attribute.name);
    }
    clone.setAttribute('data-clone', '');
    clone.classList.remove('is-active');
    const video = clone.querySelector('video');
    if (video) {
      video.removeAttribute('src');
      video.classList.remove('is-playing');
      video.muted = true;
    }
    clone.querySelector('.qvs__sound')?.setAttribute('aria-pressed', 'false');
    const play = clone.querySelector('.qvs__play');
    if (play) play.hidden = this.autoplay;
    return clone;
  }

  /* Slides die buiten beeld zijn van de volle naar de lege kant verplaatsen, zonder dat er iets verspringt. */
  rebalance(anchor) {
    if (!this.loop) return;
    const track = this.track;
    const items = Array.from(track.children);
    const index = items.indexOf(anchor);
    if (index < 0) return;

    const offsetInView = anchor.offsetLeft - track.scrollLeft;
    let before = index;
    let after = items.length - 1 - index;
    let moved = false;
    while (after - before > 1) {
      track.prepend(track.lastElementChild);
      before++;
      after--;
      moved = true;
    }
    while (before - after > 1) {
      track.append(track.firstElementChild);
      before--;
      after++;
      moved = true;
    }
    if (!moved) return;
    this.slides = Array.from(track.children);
    this.jumpTo(anchor.offsetLeft - offsetInView);
  }

  /* Direct verspringen zonder animatie. Werkt in elke browser (Safari kent niet overal behavior: 'instant'). */
  jumpTo(left) {
    const track = this.track;
    track.style.scrollBehavior = 'auto';
    track.scrollLeft = left;
    track.style.scrollBehavior = '';
  }

  scheduleSettle(delay = SETTLE_DELAY) {
    clearTimeout(this.settleTimer);
    this.settleTimer = setTimeout(this.onSettle, delay);
  }

  /*
   * Na het scrollen: enkel slides verplaatsen als de slider exact op een slide staat.
   * Zo wordt een snap-animatie van de browser (iOS) nooit onderbroken.
   * Blijft hij toch tussen twee slides hangen, dan schuift hij zelf netjes naar de dichtstbijzijnde.
   */
  onSettle() {
    clearTimeout(this.settleTimer);
    if (this.touching || !this.started) return;
    const anchor = this.anchor();
    if (!this.isAligned(anchor)) {
      if (!this.fixPending) {
        this.fixPending = true;
        this.scheduleSettle(250);
      } else {
        this.fixPending = false;
        this.scrollToSlide(anchor);
        this.scheduleSettle(600);
      }
      return;
    }
    this.fixPending = false;
    if (this.loop) this.rebalance(anchor);
  }

  isAligned(slide) {
    if (!slide) return true;
    const track = this.track;
    if (this.mobileQuery.matches) {
      const center = track.scrollLeft + track.clientWidth / 2;
      return Math.abs(slide.offsetLeft + slide.offsetWidth / 2 - center) <= 2;
    }
    const maxScroll = track.scrollWidth - track.clientWidth;
    if (track.scrollLeft >= maxScroll - 2) return true;
    return Math.abs(slide.offsetLeft - track.scrollLeft) <= 2;
  }

  anchor() {
    return this.mobileQuery.matches ? this.closestToCenter() : this.firstInView();
  }

  /* ---------- Afspelen ---------- */

  load(video) {
    if (video.getAttribute('src')) return;
    const lowQuality = this.saveData || this.slowNetwork;
    const src = lowQuality ? video.dataset.srcSd : video.dataset.srcHd;
    if (!src) return;
    video.src = src;
  }

  play(slide, video) {
    this.load(video);
    const attempt = video.play();
    if (attempt && typeof attempt.then === 'function') {
      attempt
        .then(() => {
          video.classList.add('is-playing');
          const button = slide.querySelector('.qvs__play');
          if (button) button.hidden = true;
        })
        .catch(() => {
          /* Autoplay geweigerd (bv. iOS energiebesparing): afspeelknop tonen. */
          const button = slide.querySelector('.qvs__play');
          if (button && !this.userStarted.has(slide)) button.hidden = false;
        });
    }
  }

  sync() {
    const active = this.inView && !document.hidden;
    for (const slide of this.slides) {
      const video = slide.querySelector('video');
      if (!video) continue;
      const allowed = this.autoplay || this.userStarted.has(slide);
      const shouldPlay = active && allowed && this.visible.has(slide);
      if (shouldPlay) {
        if (video.paused) this.play(slide, video);
      } else if (!video.paused) {
        video.pause();
      }
    }
  }

  /* ---------- Interactie ---------- */

  onClick(event) {
    const button = event.target.closest('button');
    if (!button || !this.contains(button)) return;
    const slide = button.closest('.qvs__slide');

    if (button.classList.contains('qvs__nav')) {
      this.go(Number(button.dataset.dir) || 1);
      return;
    }

    if (button.classList.contains('qvs__play') && slide) {
      this.userStarted.add(slide);
      button.hidden = true;
      this.visible.add(slide);
      const video = slide.querySelector('video');
      if (video) this.play(slide, video);
      return;
    }

    if (button.classList.contains('qvs__sound') && slide) {
      const turnOn = button.getAttribute('aria-pressed') !== 'true';
      this.querySelectorAll('.qvs__sound').forEach((other) => {
        other.setAttribute('aria-pressed', 'false');
        const otherVideo = other.closest('.qvs__slide')?.querySelector('video');
        if (otherVideo) otherVideo.muted = true;
      });
      const video = slide.querySelector('video');
      if (!video) return;
      button.setAttribute('aria-pressed', String(turnOn));
      video.muted = !turnOn;
      if (turnOn) {
        this.userStarted.add(slide);
        const playButton = slide.querySelector('.qvs__play');
        if (playButton) playButton.hidden = true;
        this.play(slide, video);
      }
    }
  }

  go(direction) {
    const track = this.track;

    if (!this.loop) {
      const maxScroll = track.scrollWidth - track.clientWidth;
      if (direction > 0 && track.scrollLeft >= maxScroll - 2) {
        track.scrollTo({ left: 0 });
        return;
      }
      if (direction < 0 && track.scrollLeft <= 2) {
        track.scrollTo({ left: maxScroll });
        return;
      }
    }

    const index = this.slides.indexOf(this.anchor());
    const next = Math.min(Math.max(index + direction, 0), this.slides.length - 1);
    this.scrollToSlide(this.slides[next]);
    if (!this.supportsScrollEnd) this.scheduleSettle(600);
  }

  scrollToSlide(slide, jump = false) {
    if (!slide) return;
    const track = this.track;
    const left = this.mobileQuery.matches
      ? slide.offsetLeft - (track.clientWidth - slide.offsetWidth) / 2
      : slide.offsetLeft;
    if (jump) this.jumpTo(Math.max(0, left));
    else track.scrollTo({ left: Math.max(0, left) });
  }

  firstInView() {
    const scrollLeft = this.track.scrollLeft;
    return this.slides.find((slide) => slide.offsetLeft + slide.offsetWidth / 2 > scrollLeft) || this.slides[0];
  }

  closestToCenter() {
    const center = this.track.scrollLeft + this.track.clientWidth / 2;
    let best = this.slides[0];
    let bestDistance = Infinity;
    for (const slide of this.slides) {
      const distance = Math.abs(slide.offsetLeft + slide.offsetWidth / 2 - center);
      if (distance < bestDistance) {
        best = slide;
        bestDistance = distance;
      }
    }
    return best;
  }

  onScroll() {
    if (!this.supportsScrollEnd && !this.touching) this.scheduleSettle();
    if (this.scrollFrame) return;
    this.scrollFrame = requestAnimationFrame(() => {
      this.scrollFrame = null;
      this.updateActive();
    });
  }

  updateActive() {
    const active = this.closestToCenter();
    this.slides.forEach((slide) => slide.classList.toggle('is-active', slide === active));
  }

  onBlockSelect(event) {
    const slide = this.originals.find((item) => item.contains(event.target) || item === event.target);
    if (slide) this.scrollToSlide(slide);
  }
}

if (!customElements.get('qallo-video-slider')) {
  customElements.define('qallo-video-slider', QalloVideoSlider);
}